import moment from "moment";
import { isoWeeksPerPeriod, lastPeriodOfYear } from "utils/getIsoPeriodStart";
import getIsoPeriodNumber from "utils/getIsoPeriodNumber";

// The KPI dashboards always display a rolling window of 15 four-week ISO periods.
export const kpiWindowPeriodsToShow = 15;

// A long ISO year has 53 weeks; the extra week folds into period 13.
const weeksInLongIsoYear = 53;

// Resolves the ISO period containing a date, mirroring the backend IsoPeriodService: periods are
// always 1..13, with week 53 of a 53-week ISO year folded into period 13 (which then spans five
// weeks, ending at week 53).
function resolvePeriod(date) {
    const isoWeek = moment(date).isoWeek();
    const isoYear = moment(date).isoWeekYear();
    const isoPeriod = Math.min(Math.ceil(isoWeek / isoWeeksPerPeriod), lastPeriodOfYear);
    const isoWeekStart = (isoPeriod - 1) * isoWeeksPerPeriod + 1;
    const periodStart = moment().isoWeekYear(isoYear).isoWeek(isoWeekStart).startOf("isoWeek");

    let isoWeekEnd = isoPeriod * isoWeeksPerPeriod;
    if (isoPeriod === lastPeriodOfYear && periodStart.isoWeeksInYear() === weeksInLongIsoYear) {
        isoWeekEnd = weeksInLongIsoYear;
    }

    return { isoYear: isoYear, isoPeriod: isoPeriod, isoWeekStart: isoWeekStart, isoWeekEnd: isoWeekEnd, periodStart: periodStart };
}

// Human-readable dates shown in the period-header tooltip (e.g. "15 Jun 2026").
const tooltipDateFormat = "DD MMM YYYY";
// Same date format without the year, used for the start of a same-year range (the year is shown once
// at the end of the range).
const tooltipDateFormatNoYear = "DD MMM";
// The header tooltip parts are joined by a spaced middle dot.
const tooltipSeparator = " · ";

// Builds the tooltip shown on a period column header, e.g.
// "Period 7 · ISO weeks 25–28 · 15 Jun – 12 Jul 2026". Everything is derived from the ISO year/period
// alone (not from any period object's precomputed week fields) so it works for both API and synthesised
// window periods. The ISO week/date maths reuses resolvePeriod, keeping it identical to the grid — same
// 53-week long-year fold into period 13 (which then ends at week 53).
export function formatPeriodHeaderTooltip(isoYear, isoPeriod) {
    const parts = describePeriodDates(isoYear, isoPeriod);
    return [parts.periodText, parts.weeksText, parts.datesText].join(tooltipSeparator);
}

// The parts a period's tooltip is built from: "Period 7", "ISO weeks 25–28" and "15 Jun – 12 Jul 2026".
export function describePeriodDates(isoYear, isoPeriod) {
    // The Monday of the period's first ISO week, mirroring getIsoPeriodStart; feeding it back through
    // resolvePeriod recovers the full period (week range, start date, and long-year fold).
    const startWeek = (isoPeriod - 1) * isoWeeksPerPeriod + 1;
    const periodStartDate = moment().isoWeekYear(isoYear).isoWeek(startWeek).startOf("isoWeek");
    const period = resolvePeriod(periodStartDate);

    const periodStart = period.periodStart;
    // The Sunday of the period's last ISO week — the isoWeekEnd resolvePeriod already folded for a
    // 53-week year, so this lands on the correct calendar end date.
    const periodEnd = moment().isoWeekYear(period.isoYear).isoWeek(period.isoWeekEnd).endOf("isoWeek");

    // Same calendar year → show the year once at the end of the range; spanning two years → show it on
    // both ends so each date is unambiguous.
    const sameYear = periodStart.year() === periodEnd.year();
    const startText = sameYear ? periodStart.format(tooltipDateFormatNoYear) : periodStart.format(tooltipDateFormat);
    const endText = periodEnd.format(tooltipDateFormat);

    return {
        periodText: `Period ${period.isoPeriod}`,
        weeksText: `ISO weeks ${period.isoWeekStart}–${period.isoWeekEnd}`,
        datesText: `${startText} – ${endText}`
    };
}

// Builds the full 15-period window for the KPI grid from the window anchor, merging in the periods
// the API returned. The API clips its response to the account's KPI start floor; periods missing from
// the response are padded with a synthesised period so the grid always shows the full window. Padded
// periods have no KPI values, so their cells render like any no-data cell, and carry the same
// today-relative isCurrent/isOpen flags the API sets.
//
// Marks each period isBeforeFloor: true when its ordinal (isoYear * lastPeriodOfYear + isoPeriod) is
// before the floor ordinal. Keyed on the ordinal, not on whether the period has values, so a period at
// or after the floor stays navigable even with no values. A null or unparseable floor leaves every
// period navigable.
//
// The direction is the caller's choice: the window is returned oldest-first unless newestPeriodFirst
// is set, in which case the newest period leads.
export function buildKpiPeriodWindow(responsePeriods, anchorDate, kpiStartFloor = null, newestPeriodFirst = false) {
    const currentPeriod = resolvePeriod(moment());
    const precedingPeriod = resolvePeriod(moment(currentPeriod.periodStart).subtract(1, "day"));

    let floorOrdinal = null;
    if (kpiStartFloor != null && moment(kpiStartFloor).isValid()) {
        const floorPeriod = getIsoPeriodNumber(kpiStartFloor);
        floorOrdinal = floorPeriod.isoYear * lastPeriodOfYear + floorPeriod.isoPeriod;
    }

    // Walk back from the anchor period collecting newest-first, stepping to the period containing
    // the day before the period's start (robust across ISO year boundaries and five-week periods).
    const newestFirst = [];
    let period = resolvePeriod(anchorDate);
    for (let index = 0; index < kpiWindowPeriodsToShow; index++) {
        newestFirst.push(period);
        period = resolvePeriod(moment(period.periodStart).subtract(1, "day"));
    }

    if (!newestPeriodFirst) {
        newestFirst.reverse();
    }

    return newestFirst.map((windowPeriod) => {
        const isBeforeFloor = floorOrdinal != null
            && (windowPeriod.isoYear * lastPeriodOfYear + windowPeriod.isoPeriod) < floorOrdinal;

        const responsePeriod = responsePeriods.find(
            (candidate) => candidate.isoYear === windowPeriod.isoYear && candidate.isoPeriod === windowPeriod.isoPeriod
        );

        if (responsePeriod != null) {
            // Spread so the API object is not mutated; the window copy carries the navigability flag.
            // Override the week range with the FE-computed values (from the same resolvePeriod maths the
            // header tooltip uses) so the grid header and its tooltip can never disagree on a long-year
            // edge, and every column derives its weeks from one source regardless of API vs synthesised.
            return { ...responsePeriod, isoWeekStart: windowPeriod.isoWeekStart, isoWeekEnd: windowPeriod.isoWeekEnd, isBeforeFloor: isBeforeFloor };
        }

        return {
            isoYear: windowPeriod.isoYear,
            isoPeriod: windowPeriod.isoPeriod,
            isoWeekStart: windowPeriod.isoWeekStart,
            isoWeekEnd: windowPeriod.isoWeekEnd,
            isCurrent: windowPeriod.isoYear === currentPeriod.isoYear && windowPeriod.isoPeriod === currentPeriod.isoPeriod,
            isOpen: (windowPeriod.isoYear === currentPeriod.isoYear && windowPeriod.isoPeriod === currentPeriod.isoPeriod)
                || (windowPeriod.isoYear === precedingPeriod.isoYear && windowPeriod.isoPeriod === precedingPeriod.isoPeriod),
            hasTrigger: false,
            isSmallOperatorCapped: false,
            isBeforeFloor: isBeforeFloor
        };
    });
}

// True when a column header should name its ISO year: the earliest period shown, and any period whose
// year differs from the period before it in time. Compared in time, not by screen neighbour, so the
// year lands on the same columns in either column order.
export function showsIsoYear(periods, index) {
    const ordinal = (windowPeriod) => windowPeriod.isoYear * lastPeriodOfYear + windowPeriod.isoPeriod;
    const period = periods[index];
    const previousPeriod = periods.find((candidate) => ordinal(candidate) === ordinal(period) - 1);
    return previousPeriod == null || previousPeriod.isoYear !== period.isoYear;
}

// Lower bound for period navigation: stop when the oldest visible column is the account's
// KPI start floor. The window is 15 periods wide, so that anchor is the floor + 14 periods,
// capped at today so it never crosses the maxDate (current day) bound. Null or unparseable floor → undefined,
// so FormDatePeriodSelector falls back to its default 30-month lower bound.
export function resolveSelectorMinDate(kpiStartFloor, now = moment()) {
    if (kpiStartFloor == null) {
        return undefined;
    }
    const floor = moment(kpiStartFloor);
    if (!floor.isValid()) {
        return undefined;
    }
    const floorAnchor = floor.add((kpiWindowPeriodsToShow - 1) * isoWeeksPerPeriod, "weeks");
    return moment.min(floorAnchor, moment(now)).toDate();
}

// The raw account KPI start floor, as a Date, for the calendar's lower bound — the calendar can reach
// the actual account floor (unlike the arrow/anchor floor, which sits floor + 14 periods later). Null
// or unparseable floor → undefined, so the calendar falls back to the component's default lower bound.
export function resolveCalendarMinDate(kpiStartFloor) {
    if (kpiStartFloor == null) {
        return undefined;
    }
    const floor = moment(kpiStartFloor);
    if (!floor.isValid()) {
        return undefined;
    }
    return floor.toDate();
}

// True when the given ISO year/period falls entirely before the period containing the given date — so
// the period the date itself falls in is not before it. Compares period ordinals (isoYear *
// lastPeriodOfYear + isoPeriod). A null or unparseable date returns false, imposing no lower bound.
export function isPeriodBeforeDate(isoYear, isoPeriod, date) {
    if (date == null) {
        return false;
    }
    const boundary = moment(date);
    if (!boundary.isValid()) {
        return false;
    }
    const boundaryPeriod = getIsoPeriodNumber(date);
    return isoYear * lastPeriodOfYear + isoPeriod < boundaryPeriod.isoYear * lastPeriodOfYear + boundaryPeriod.isoPeriod;
}

// True when the given ISO year/period is at or before the account's KPI start floor period — the point
// past which there is no data to drill into. Compares period ordinals (isoYear * lastPeriodOfYear +
// isoPeriod). A null or unparseable floor returns false, imposing no lower bound.
export function isPeriodAtOrBeforeFloor(isoYear, isoPeriod, kpiStartFloor) {
    if (kpiStartFloor == null) {
        return false;
    }
    const floor = moment(kpiStartFloor);
    if (!floor.isValid()) {
        return false;
    }
    const floorPeriod = getIsoPeriodNumber(kpiStartFloor);
    return isoYear * lastPeriodOfYear + isoPeriod <= floorPeriod.isoYear * lastPeriodOfYear + floorPeriod.isoPeriod;
}
